// THE grocery list: recipe cards with servings, aisle sections, checked section,
// ➕ merged rows across recipes, ➕ loose items, Move to Pantry.
import React, { useMemo, useState } from 'react';
import { Pressable, Text, View, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useHeader, useToday } from '../hooks';
import { useShopping } from '../store/shopping';
import { useAllIngredients, useAllUtensils, useLibrary, useRecipeLookup } from '../store/library';
import { cardProgress, groceryRows, type GroceryRow } from '../logic/grocery';
import { formatQuantity, ingredientLine, pluralTitle } from '../logic/format';
import { resolveUnit } from '../logic/units';
import i18n from '../i18n';
import Icon from '../components/Icon';
import Photo from '../components/Photo';
import Sheet from '../components/Sheet';
import ItemSearchBox from '../components/ItemSearchBox';
import { confirm } from '../components/Dialogs';
import { Swap } from '../components/Motion';
import { Caption, EmptyState, IconButton, Row, Screen, SectionHeader, Stepper } from '../components/ui';
import { INGREDIENT_AISLES, UTENSIL_AISLES, type Id } from '../types';
import { font, radius, spacing, usePalette } from '../theme';

export default function GroceryScreen() {
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const today = useToday();
  const { grocery, setChecked, setCardServings, moveToPantry, addLooseItem, removeFromGrocery } = useShopping();
  const lookup = useRecipeLookup();
  const ingredients = useAllIngredients();
  const utensils = useAllUtensils();
  const units = useLibrary(s => s.units);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);

  const servings = useMemo(() => Object.fromEntries(grocery.recipes.map(c => [c.recipeId, lookup(c.recipeId)?.servings ?? c.servings])), [grocery.recipes, lookup]);
  const rows = useMemo(() => groceryRows(grocery, servings, units), [grocery, servings, units]);
  const anyChecked = rows.some(r => r.checked);

  useHeader(
    {
      title: t('grocery.title'),
      headerRight: () => (
        <Row gap={spacing.m}>
          <IconButton icon="plus" label={t('grocery.addItem')} onPress={() => setAdding(true)} />
          <IconButton
            icon="pantry"
            label={t('grocery.moveToPantry')}
            disabled={!anyChecked}
            onPress={() => {
              moveToPantry(servings, units, today);
              nav.navigate('Pantry');
            }}
          />
          <IconButton icon={editing ? 'check' : 'edit'} label={editing ? t('common.done') : t('common.edit')} onPress={() => setEditing(e => !e)} />
        </Row>
      ),
    },
    [anyChecked, servings, units, today, editing, t],
  );

  const title = (row: GroceryRow) => (row.kind === 'ingredient' ? ingredients[row.refId]?.title : utensils[row.refId]?.title) ?? row.refId;
  const rowText = (row: GroceryRow) => {
    const unit = resolveUnit(row.unit, units);
    const amount = formatQuantity(row.amount, unit);
    const name = pluralTitle(title(row), row.amount, !!unit, i18n.language);
    return unit ? ingredientLine(amount, unit.symbol, name, t('common.of')) : `${amount} ${name}`;
  };

  const cards = [...grocery.recipes].sort((a, b) => Number(cardProgress(a).done) - Number(cardProgress(b).done));
  const unchecked = rows.filter(r => !r.checked);
  const checked = rows.filter(r => r.checked);
  const groups = [
    ...INGREDIENT_AISLES.map(a => ({ key: a, label: t(`aisles.${a}`), rows: unchecked.filter(r => r.kind === 'ingredient' && (ingredients[r.refId]?.aisle ?? 'other') === a) })),
    ...UTENSIL_AISLES.map(a => ({ key: `u.${a}`, label: t(`aisles.${a}`), rows: unchecked.filter(r => r.kind === 'utensil' && (utensils[r.refId]?.aisle ?? 'other') === a) })),
  ].filter(g => g.rows.length);

  const renderRow = (row: GroceryRow) => {
    const looseOnly = row.recipeIds.length === 0;
    return (
      <Pressable
        key={row.key}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: row.checked }}
        onPress={() => setChecked(row.itemIds, !row.checked)}
        style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: spacing.m, paddingVertical: spacing.m, paddingHorizontal: spacing.l, backgroundColor: p.card, borderRadius: radius.l, opacity: row.checked ? 0.5 : pressed ? 0.7 : 1 })}>
        <Icon name={row.kind === 'ingredient' ? 'ingredient' : 'utensil'} color={p.secondaryText} />
        <View style={{ flex: 1, gap: 4 }}>
          <Text style={{ fontSize: font.body, color: p.text, textDecorationLine: row.checked ? 'line-through' : 'none', fontVariant: ['tabular-nums'] }}>{rowText(row)}</Text>
          {row.recipeIds.length ? <Caption>{row.recipeIds.map(id => lookup(id)?.title ?? '').join(' · ')}</Caption> : null}
        </View>
        {/* Check circle and delete share one spot, so editing does not rewrap the text. */}
        <Swap
          showB={editing && looseOnly}
          style={{ width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}
          a={<Icon name={row.checked ? 'checkCircle' : 'circle'} color={row.checked ? p.success : p.secondaryText} />}
          b={<IconButton icon="trash" color={p.danger} label={t('common.delete')} onPress={() => removeFromGrocery([], row.itemIds)} />}
        />
      </Pressable>
    );
  };

  if (!grocery.recipes.length && !grocery.looseItems.length) {
    return (
      <>
        <EmptyState icon="grocery" title={t('grocery.title')} body={t('grocery.empty')} />
        <AddSheet visible={adding} onClose={() => setAdding(false)} onAdd={addLooseItem} />
      </>
    );
  }

  return (
    <>
      <Screen>
        {cards.length ? (
          <View style={{ gap: spacing.s }}>
            <SectionHeader title={t('grocery.shoppingFor')} />
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: spacing.m }} style={{ marginHorizontal: -spacing.l, paddingHorizontal: spacing.l }}>
              {cards.map(card => {
                const r = lookup(card.recipeId);
                const prog = cardProgress(card);
                return (
                  <View key={card.id} style={{ width: 230, backgroundColor: p.card, borderRadius: radius.l, padding: spacing.m, gap: spacing.s, opacity: prog.done ? 0.5 : 1 }}>
                    <Photo media={r?.image} seed={card.recipeId} style={{ width: '100%', aspectRatio: 2 }} />
                    <Caption>{t('grocery.servingsOf', { count: card.servings })}</Caption>
                    <Text style={{ fontSize: font.body, fontWeight: '700', color: p.text, textDecorationLine: prog.done ? 'line-through' : 'none' }} numberOfLines={2}>{r?.title ?? '—'}</Text>
                    <Caption>{t('grocery.progress', prog)}</Caption>
                    <Row>
                      <Swap
                        showB={editing}
                        bStyle={{ alignItems: 'flex-start' }}
                        a={<Stepper label={t('recipe.servings')} value={card.servings} min={1} max={99} onChange={v => setCardServings(card.id, v)} />}
                        b={
                          <IconButton
                            icon="trash"
                            color={p.danger}
                            label={t('common.delete')}
                            onPress={async () => {
                              if (await confirm(t('common.deleteConfirmTitle'), r?.title)) removeFromGrocery([card.id], []);
                            }}
                          />
                        }
                      />
                    </Row>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        ) : null}
        {groups.map(g => (
          <View key={g.key} style={{ gap: spacing.s }}>
            <SectionHeader title={g.label} color={g.key === 'other' ? p.warning : undefined} />
            {g.rows.map(renderRow)}
          </View>
        ))}
        {checked.length ? (
          <View style={{ gap: spacing.s }}>
            <SectionHeader title={t('grocery.checked')} />
            {checked.map(renderRow)}
          </View>
        ) : null}
      </Screen>
      <AddSheet visible={adding} onClose={() => setAdding(false)} onAdd={addLooseItem} />
    </>
  );
}

function AddSheet({ visible, onClose, onAdd }: { visible: boolean; onClose: () => void; onAdd: ReturnType<typeof useShopping.getState>['addLooseItem'] }) {
  const { t } = useTranslation();
  const none = useMemo(() => new Set<Id>(), []);
  return (
    <Sheet visible={visible} onClose={onClose} title={t('grocery.addItem')}>
      <Caption>{t('grocery.addItemPlaceholder')}</Caption>
      <ItemSearchBox
        excludeIngredients={none}
        excludeUtensils={none}
        onPick={pick => {
          onAdd(pick.kind, pick.id, pick.kind === 'ingredient' ? pick.quantity.amount : { value: Math.max(1, pick.quantity.amount.value), places: 0 }, pick.kind === 'ingredient' ? pick.quantity.unit : null);
          onClose();
        }}
      />
    </Sheet>
  );
}
